import { Flex, Input, Tag, TagCloseButton, TagLabel, useToast } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { KeyboardEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLongPress } from 'use-long-press';

import CustomToast from 'components/CustomToast';
import { UpdateDimensionItem } from 'models/requestBody/updateDimensionItem.interface';
import { CHART_COLORS } from 'projectConstants';
import { getDimensionItems } from 'redux/IndicatorPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import apiService from 'services';
import PlanPermission from 'container/PlanPermission';
import DeleteModal from 'components/Modals/DeleteModal';

export interface Properties {
  row: string;
  planPermission: boolean;
  currentDimensionItemIndex: number;
  refetchRequest: () => void;
}
const ItemActions = ({
  row,
  planPermission,
  currentDimensionItemIndex,
  refetchRequest,
}: Properties) => {
  const dimensionItems = useSelector(getDimensionItems);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [value, setValue] = useState(row);
  const toast = useToast();
  const [showDelete, setShowDelete] = useState(false);
  const onToggleDeleteModal = () => {
    setIsDeleteModal((previousState) => !previousState);
  };
  const baseScenario = useSelector(getBaseScenario);

  const { mutate: rename } = useMutation(
    ({ item_id, request_body }: UpdateDimensionItem) =>
      apiService.update_dimension_item({ item_id, request_body }),
    {
      onSuccess: () => {
        refetchRequest();
        toast({
          duration: 2500,
          render: () => <CustomToast title={'Updated Successfully'} status='success' />,
        });
      },
      onError: (response: any) => {
        toast({
          duration: 2500,
          render: () => (
            <CustomToast
              title={response?.response?.data?.message || 'Something went wrong..'}
              status='error'
            />
          ),
        });
        setValue(row);
      },
    },
  );
  const { mutate: remove } = useMutation(
    (itemId: number) =>
      apiService.delete_dimension_item(itemId, {
        params: {
          scenario_id: baseScenario?.id,
        },
      }),
    {
      onSuccess: () => {
        refetchRequest();
        onToggleDeleteModal();
      },
    },
  );
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (value.trim() !== '') {
        rename({
          item_id: dimensionItems?.items[currentDimensionItemIndex]?.id,
          request_body: { name: value, scenario_id: baseScenario?.id },
        });
        setIsEditMode(false);
      } else {
        toast({
          duration: 2500,
          render: () => <CustomToast title='Please enter a valid name' status='error' />,
        });
      }
    }
  };

  const onDeleteItem = () => {
    remove(dimensionItems?.items[currentDimensionItemIndex].id);
  };
  const longClickCheck = useLongPress(() => {
    setIsEditMode(true);
  });
  return (
    <>
      <Flex
        alignItems={'center'}
        textAlign={'center'}
        onMouseEnter={() => setShowDelete(true)}
        onMouseLeave={() => setShowDelete(false)}
        onClick={(event: any) => event.preventDefault()}
      >
        <Tag
          margin={'0 auto'}
          position='relative'
          pr='2'
          cursor='pointer'
          onDoubleClick={() => setIsEditMode(true)}
          {...longClickCheck()}
          transition='all 0.25s ease'
          bg='transparent'
          border={'none'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          {!planPermission && isEditMode ? (
            <Input
              focusBorderColor={CHART_COLORS[currentDimensionItemIndex]}
              h='8.5'
              w='24'
              fontSize='2xs'
              textAlign='center'
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={onKeyDown}
              onBlur={() => setIsEditMode(false)}
              onClick={(event) => event.stopPropagation()}
              autoFocus
            />
          ) : (
            <>
              <TagLabel
                lineHeight='4'
                textAlign='start'
                fontSize={'xs'}
                textOverflow={'ellipsis'}
                maxW={'90px'}
                overflow={'hidden'}
              >
                {row}
              </TagLabel>
              {showDelete && (
                <TagCloseButton
                  display='flex'
                  style={{ cursor: planPermission ? 'not-allowed' : 'pointer' }}
                  onClick={
                    planPermission
                      ? undefined
                      : (event) => {
                          event.stopPropagation();
                          onToggleDeleteModal();
                        }
                  }
                />
              )}
            </>
          )}
        </Tag>
      </Flex>
      <DeleteModal
        isOpen={isDeleteModal}
        onClose={onToggleDeleteModal}
        itemName={row}
        onDelete={onDeleteItem}
      />
    </>
  );
};
export default PlanPermission(ItemActions);
